import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FanAvatar from "../components/FanAvatar";
import ImageCropperModal from "../components/ImageCropperModal";
import { pickProfilePhoto } from "../lib/pickProfilePhoto";
import { dataUrlToAvatarBlob } from "../lib/fanPhotoUpload";
import {
  INSTAGRAM_KEY,
  NICKNAME_KEY,
  readIdentity,
  writeIdentity,
} from "../lib/fanProfile";
import {
  ensureFanSession,
  fanFetchMe,
  fanPatchMe,
  fanUploadAvatar,
  getFanUser,
  getIgDmVerifyCapable,
  getPhotosCapable,
  isFanSignedIn,
} from "../lib/fanAccount";
import { clearAppRole } from "../lib/appRole";
import FanConnectFlow from "./FanConnectFlow";
import "./FanNamePage.css";
import { sanitizeInstagramHandle } from "../utils/instagramHandle";
import InstagramGlyph from "../components/InstagramGlyph";
import { t } from "../i18n";

// App fan mode — nickname screen (locked r6 §4, fourth phone; founder decision:
// nickname and Instagram are two separate rows, not one combined field):
// identity bubble, "What should we call you?", the name PRE-FILLED from the
// Google/Apple account, an optional @instagram row, and a green "Let's go".
//
// Saving matches the profile editor: device storage always mirrors the
// latest identity, and a signed-in fan PATCHes /fans/me too. "Tap to
// change" photo appears ONLY when signed in AND the server photo pipeline
// is live (photos may still be dark at launch).
//
// "Connect Instagram" (1.5.2): while the server has "Verify with a DM" on for
// this fan (GET /fans/me igDmVerify), the page is FanConnectFlow instead —
// the nickname, then Instagram connected by a message, never typed. Off, it
// is this screen exactly as before.

const FanNamePage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState(
    () => getFanUser()?.name || readIdentity(NICKNAME_KEY)
  );
  const [ig, setIg] = useState(
    () => getFanUser()?.instagram || readIdentity(INSTAGRAM_KEY)
  );
  // Once the fan types, the boot-session prefill below must never replace
  // their keystrokes.
  const dirtyRef = useRef(false);

  const [signedIn, setSignedIn] = useState(isFanSignedIn());
  const [photosCapable, setPhotosCapable] = useState(getPhotosCapable());
  const [igDmCapable, setIgDmCapable] = useState(getIgDmVerifyCapable());
  // The normal way in (claim → here) already read the account, so its
  // capabilities are known; a cold load waits for them rather than flash
  // the old screen and then swap.
  const warm = useRef(!!getFanUser()).current;
  const [ready, setReady] = useState(warm);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  // The normal flow (claim → here) arrives with the session warm, so the
  // initial reads above already have the account identity. A COLD load
  // (app relaunched mid-onboarding) starts with empty module state —
  // re-read once the boot refresh answers, or an untouched "Let's go"
  // could save empty fields over the account's real ones.
  useEffect(() => {
    let cancelled = false;
    ensureFanSession()
      // A cold load only refreshed the session: read the account too, so the
      // capabilities (Connect Instagram, photos) are known here as well.
      .then(() => (!warm && isFanSignedIn() ? fanFetchMe().catch(() => null) : null))
      .then(() => {
        if (cancelled) return;
        setSignedIn(isFanSignedIn());
        setPhotosCapable(getPhotosCapable());
        setIgDmCapable(getIgDmVerifyCapable());
        setReady(true);
        if (!dirtyRef.current) {
          const account = getFanUser();
          const freshName = account?.name || readIdentity(NICKNAME_KEY);
          const freshIg = account?.instagram || readIdentity(INSTAGRAM_KEY);
          if (freshName) setName(freshName);
          if (freshIg) setIg(freshIg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [warm]);

  const account = getFanUser();
  const trimmedName = name.trim();
  const bubbleName = trimmedName || (ig ? `@${ig}` : "");
  const canTapPhoto = signedIn && photosCapable && !avatarBusy;

  const handleAvatarTap = async () => {
    if (!canTapPhoto) return;
    const dataUrl = await pickProfilePhoto();
    if (dataUrl) setCropSrc(dataUrl);
  };

  const handleCropConfirm = async (croppedDataUrl: string) => {
    setCropSrc(null);
    setAvatarBusy(true);
    setAvatarError("");
    try {
      const blob = await dataUrlToAvatarBlob(croppedDataUrl);
      // The upload updates the module fan store; the busy-state re-render
      // below re-reads getFanUser() and shows the new photo.
      await fanUploadAvatar(blob);
    } catch (err: any) {
      const msg = err?.response?.data?.msg;
      setAvatarError(
        msg === "photo_rejected"
          ? t("fanNamePage.thatPhotoDidnT")
          : msg === "file_too_large"
          ? t("fanNamePage.thatPhotoIsToo")
          : t("fanNamePage.couldnTUploadTry")
      );
    } finally {
      setAvatarBusy(false);
    }
  };

  const go = () => {
    const nick = trimmedName.slice(0, 50);
    const handle = sanitizeInstagramHandle(ig);
    // Device always mirrors the latest identity (editor contract) — the
    // signed-out fallback stays fresh and the header chip re-reads.
    writeIdentity(NICKNAME_KEY, nick);
    writeIdentity(INSTAGRAM_KEY, handle);
    if (isFanSignedIn()) {
      // Fire-and-forget: the device copy is already saved, and the next
      // editor auto-save self-heals a dropped PATCH. Never block the door.
      fanPatchMe({ name: nick, instagram: handle }).catch(() => {});
    }
    navigate("/fan", { replace: true });
  };

  const cropper = cropSrc && (
    <ImageCropperModal
      imageSrc={cropSrc}
      cropShape="round"
      aspect={1}
      confirmLabel={t("fanNamePage.useThisPhoto")}
      onCancel={() => setCropSrc(null)}
      onConfirm={handleCropConfirm}
    />
  );

  if (!ready) return <div className="fanname-page" />;

  if (signedIn && igDmCapable) {
    return (
      <>
        <FanConnectFlow
          displayName={trimmedName}
          avatarUrl={account?.avatarUrl || null}
          nameFields={
            <>
              <button
                type="button"
                className="fanname-ava-tap"
                onClick={handleAvatarTap}
                disabled={!canTapPhoto}
                aria-label={account?.avatarUrl ? t("fanNamePage.changeYourPhoto") : t("fanNamePage.addYourPhoto")}
              >
                <FanAvatar className="fanname-ava" name={trimmedName} avatarUrl={account?.avatarUrl} silhouetteSize={34} />
              </button>
              {canTapPhoto && (
                <div className="fanname-ava-cap">{avatarBusy ? t("fanNamePage.uploading") : t("fanNamePage.tapToChange")}</div>
              )}
              {avatarError && <div className="fanname-error">{avatarError}</div>}
              <h1 className="fcf-title fcf-title--name">{t("fanConnect.welcome")}</h1>
              <p className="fcf-sub">{t("fanConnect.setUp")}</p>
              <label className="fanname-label fcf-name-label" htmlFor="fanname-nick">{t("fanNamePage.nickname")}</label>
              <input
                id="fanname-nick"
                className="fanname-input"
                type="text"
                placeholder={t("fanNamePage.yourName")}
                value={name}
                maxLength={50}
                autoComplete="off"
                autoCapitalize="words"
                onChange={(e) => {
                  dirtyRef.current = true;
                  setName(e.target.value);
                }}
              />
              <p className="fcf-name-help">{t("fanConnect.nicknameHelp")}</p>
            </>
          }
          onNameDone={() => {
            const nick = trimmedName.slice(0, 50);
            writeIdentity(NICKNAME_KEY, nick);
            // Fire-and-forget, like "Let's go": the name only. Instagram comes
            // from Instagram on the next step, never from typing.
            fanPatchMe({ name: nick }).catch(() => {});
          }}
          onLeave={() => {
            // Same door as the claim screen's Back: not a fan after all.
            clearAppRole();
            navigate("/welcome?role=1", { replace: true });
          }}
          onConnected={(username) => writeIdentity(INSTAGRAM_KEY, username)}
          onFinish={() => navigate("/fan", { replace: true })}
        />
        {cropper}
      </>
    );
  }

  return (
    <div className="fanname-page">
      <div className="fanname-inner">
        <button
          type="button"
          className="fanname-ava-tap"
          onClick={handleAvatarTap}
          disabled={!canTapPhoto}
          aria-label={account?.avatarUrl ? t("fanNamePage.changeYourPhoto") : t("fanNamePage.addYourPhoto")}
        >
          <FanAvatar
            className="fanname-ava"
            name={bubbleName}
            avatarUrl={account?.avatarUrl}
            silhouetteSize={34}
          />
        </button>
        {canTapPhoto && (
          <div className="fanname-ava-cap">
            {avatarBusy ? t("fanNamePage.uploading") : t("fanNamePage.tapToChange")}
          </div>
        )}
        {avatarError && <div className="fanname-error">{avatarError}</div>}

        <h1 className="fanname-title">{t("fanNamePage.whatShouldWeCall")}</h1>
        <label className="fanname-label" htmlFor="fanname-nick">{t("fanNamePage.nickname")}</label>
        <input
          id="fanname-nick"
          className="fanname-input"
          type="text"
          placeholder={t("fanNamePage.yourName")}
          value={name}
          maxLength={50}
          autoComplete="off"
          autoCapitalize="words"
          onChange={(e) => {
            dirtyRef.current = true;
            setName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") go();
          }}
        />
        {/* Instagram's coloured glyph by the label (founder, 1.5.2 b50), as on the talent's Edit profile. */}
        <label className="fanname-label" htmlFor="fanname-ig">
          <InstagramGlyph size={14} className="fanname-ig-glyph" />
          {t("fanNamePage.instagramOptional")}
        </label>
        <input
          id="fanname-ig"
          className="fanname-input fanname-input--ig"
          type="text"
          placeholder={t("fanNamePage.yourhandle")}
          value={ig ? `@${ig}` : ""}
          maxLength={31}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          onChange={(e) => {
            dirtyRef.current = true;
            setIg(sanitizeInstagramHandle(e.target.value));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") go();
          }}
        />
        <p className="fanname-sub">{t("fanNamePage.thisIsTheName")}</p>
        <button type="button" className="fanname-go" onClick={go}>
          {t("fanNamePage.letSGo")}
        </button>
      </div>

      {cropper}
    </div>
  );
};

export default FanNamePage;
